import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Stethoscope, X } from 'lucide-react';
import { SearchableSelect } from './searchable-select';
import {
  useActiveDesignations,
  useActiveSpecializations,
  useCreateDoctor,
  useDepartments,
} from '../../services/httpServices/queries';
import { doctorService } from '../../services/httpServices/doctorService';

/** A doctor created inline from an admission form. */
export interface CreatedDoctor {
  id: string;
  name: string;
}

interface OutsideDoctorModalProps {
  open: boolean;
  /** Text typed into the doctor search box, pre-filled as the name. */
  initialName?: string;
  /** Digits typed into the search box, pre-filled as the phone number. */
  initialPhone?: string;
  onClose: () => void;
  /** Fired once the doctor exists on the server, so the caller can select it. */
  onCreated: (doctor: CreatedDoctor) => void;
}

/**
 * Quick "outside doctor" entry used by every admission doctor field (primary
 * doctor on registration, and both doctor fields when editing a stay). Creates a
 * regular doctor record (Employee of type `doctor`) so the stay can reference it
 * like any in-house doctor, then hands it back to the caller to select.
 */
export function OutsideDoctorModal({
  open,
  initialName = '',
  initialPhone = '',
  onClose,
  onCreated,
}: OutsideDoctorModalProps) {
  const createDoctor = useCreateDoctor();
  const { data: departmentsData } = useDepartments();
  const { data: designationsData } = useActiveDesignations();
  const { data: specializationsData } = useActiveSpecializations();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    departmentId: '',
    designationId: '',
    specializationIds: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed from the search box each time the dialog opens, never from the last
  // saved doctor, so a second entry starts clean.
  useEffect(() => {
    if (!open) return;
    setForm({
      name: initialName || '',
      phone: initialPhone || '',
      departmentId: '',
      designationId: '',
      specializationIds: [],
    });
    setError(null);
  }, [open, initialName, initialPhone]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    const { name, phone, departmentId, designationId, specializationIds } = form;
    if (!name.trim()) {
      setError('Doctor name is required');
      return;
    }
    setSaving(true);
    try {
      // Reuse an existing record instead of creating a near-duplicate: the
      // admission can point at the doctor already on file.
      const existing = await doctorService.getDoctors(1, 10, {
        search: phone || name,
      });
      const match = existing.data?.find(
        (d: any) =>
          d.name?.toLowerCase() === name.trim().toLowerCase() ||
          (phone && d.phone === phone.trim()),
      );
      if (match) {
        setError(
          `A doctor named "${match.name}" already exists. Please select from the list.`,
        );
        return;
      }

      const result = await createDoctor.mutateAsync({
        name: name.trim(),
        phone: phone.trim() || undefined,
        departmentId: departmentId || undefined,
        designationId: designationId || undefined,
        specializationIds:
          specializationIds.length > 0 ? specializationIds : undefined,
        revenueSharePercent: 0,
        type: 'doctor',
        isActive: true,
      });

      if (!result.data?.id) {
        setError('Failed to create doctor. Please try again.');
        return;
      }
      onCreated({ id: result.data.id, name: result.data.name || name.trim() });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create doctor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Stethoscope size={17} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-text">
                Quick Outside Doctor Entry
              </h2>
              <p className="text-[11px] text-text-muted">
                Added to the doctor list and selected on this admission
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 p-5">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">
              Doctor Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((d) => ({ ...d, name: e.target.value }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">
              Phone / Mobile
            </label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm((d) => ({ ...d, phone: e.target.value }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-text-muted">
              Department
            </label>
            <SearchableSelect
              value={form.departmentId}
              onChange={(value) => setForm((d) => ({ ...d, departmentId: value }))}
              options={(departmentsData?.data || []).map((d: any) => ({
                value: d.id,
                label: d.name,
              }))}
              placeholder="Select department..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-text-muted">
                Designation
              </label>
              <SearchableSelect
                value={form.designationId}
                onChange={(value) => setForm((d) => ({ ...d, designationId: value }))}
                options={(designationsData?.data || []).map((d: any) => ({
                  value: d.id,
                  label: d.name,
                }))}
                placeholder="Select..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-text-muted">
                Specialization
              </label>
              <SearchableSelect
                value={form.specializationIds[0] || ''}
                onChange={(value) =>
                  setForm((d) => ({ ...d, specializationIds: value ? [value] : [] }))
                }
                options={(specializationsData?.data || []).map((s: any) => ({
                  value: s.id,
                  label: s.label,
                }))}
                placeholder="Select..."
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Save Doctor
          </button>
        </div>
      </div>
    </div>
  );
}
